import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { GET_RECIPE_DRIFT_SUGGESTIONS } from "./graphql";

function formatDate(d) {
  return d.toISOString().split("T")[0];
}

function defaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 29); // last 30 days - patterns need a longer window than the 7-day default elsewhere
  return { startDate: formatDate(start), endDate: formatDate(end) };
}

const RecipeDriftSuggestions = () => {
  const [{ startDate, endDate }, setDateRange] = useState(defaultDateRange());

  const { data, loading, error } = useQuery(GET_RECIPE_DRIFT_SUGGESTIONS, {
    variables: { startDate, endDate },
    fetchPolicy: "network-only",
  });

  const suggestions = data?.getRecipeDriftSuggestions?.suggestions || [];

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">
              Recipe Drift Suggestions
            </h3>
            <p className="text-xs text-gray-500 mt-1 max-w-xl">
              Categories employees keep manually adding to a service outside its configured
              recipe. A high order count across multiple employees is a signal the recipe
              itself is missing something - go update it in the service's recipe editor if so.
              This list is read-only; it never changes the recipe.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
              className="px-3 py-2 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={formatDate(new Date())}
              onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
              className="px-3 py-2 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>
        </div>

        {loading && <p className="text-sm text-gray-500">Loading…</p>}
        {error && <p className="text-sm text-red-600">{error.message}</p>}
        {!loading && !error && suggestions.length === 0 && (
          <p className="text-sm text-gray-500">
            No ad-hoc additions outside configured recipes in this range.
          </p>
        )}
        {!loading && suggestions.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-2">Service</th>
                  <th className="px-4 py-2">Category</th>
                  <th className="px-4 py-2">Orders</th>
                  <th className="px-4 py-2">Total Units Added</th>
                  <th className="px-4 py-2">Employees Involved</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {suggestions.map((s) => (
                  <tr key={`${s.serviceId}:${s.categoryId}`}>
                    <td className="px-4 py-2 font-medium">{s.serviceName}</td>
                    <td className="px-4 py-2">{s.categoryName}</td>
                    <td
                      className={`px-4 py-2 font-semibold ${
                        s.orderCount >= 5 ? "text-amber-700" : "text-gray-700"
                      }`}
                    >
                      {s.orderCount}
                    </td>
                    <td className="px-4 py-2">{s.totalUnitsAdded}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">
                      {s.employeeIds.length} employee{s.employeeIds.length === 1 ? "" : "s"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecipeDriftSuggestions;
